import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Play, X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

const videos = [
  {
    id: "vg6el1FiBU0",
    title: "The Intelligence Age: AI at Enterprise Scale",
    venue: "Executive Keynote · PwC Global",
    type: "full",
    tag: "Keynote",
  },
  {
    id: "XUW3c8w6eSY",
    title: "Responsible AI: The Framework That Works",
    venue: "Global AI Leadership Forum",
    type: "full",
    tag: "Framework",
  },
  {
    id: "MS35zJVAzGE",
    title: "Human-Led, Tech-Powered Transformation",
    venue: "Executive Roundtable",
    type: "full",
    tag: "Leadership",
  },
  {
    id: "_66vfneabic",
    title: "FranklinCovey On Leadership — Episode 518",
    venue: "FranklinCovey Podcast",
    type: "full",
    tag: "Podcast",
  },
  {
    id: "I6qW80ruHac",
    title: "Your People Are Your AI Advantage",
    venue: "Quick Take · 60 Seconds",
    type: "short",
    tag: "Short",
  },
  {
    id: "gxj0zLuhJ3Y",
    title: "From 10 Pilots to Production",
    venue: "Quick Take · 60 Seconds",
    type: "short",
    tag: "Short",
  },
];

function VideoModal({ video, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 sm:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-5xl"
        initial={{ scale: 0.88, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.88, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4 px-1 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C4963B] font-semibold mb-1">{video.venue}</p>
            <h3 className="text-white font-['Cormorant_Garamond'] text-lg sm:text-2xl leading-tight">{video.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] text-white/50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className={`bg-black overflow-hidden rounded-sm shadow-2xl shadow-black/80 ${video.type === "short" ? "max-w-xs mx-auto aspect-[9/16]" : "aspect-video"}`}>
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

function VideoCard({ video, onPlay }) {
  const [hovered, setHovered] = useState(false);
  const thumbHq = `https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`;
  const thumbFb = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;

  const tagColors = {
    Keynote: "bg-[#C4963B] text-[#0A0B0E]",
    Framework: "bg-[#2A7F7A]/80 text-white",
    Leadership: "bg-[#C4963B]/80 text-[#0A0B0E]",
    Podcast: "bg-white/10 text-white/80 border border-white/10",
    Short: "bg-[#C4963B] text-[#0A0B0E]",
  };

  return (
    <motion.div
      className="flex-shrink-0 cursor-pointer group"
      style={{ width: video.type === "short" ? "200px" : "320px" }}
      onClick={onPlay}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.25 }}
    >
      <div
        className={`relative overflow-hidden rounded-sm mb-4 bg-[#1E2229] ${video.type === "short" ? "aspect-[9/16]" : "aspect-video"}`}
      >
        <img
          src={thumbHq}
          alt={video.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => { e.target.src = thumbFb; }}
          loading="lazy"
        />
        {/* Overlay on hover */}
        <motion.div
          className="absolute inset-0 bg-black/50 flex items-center justify-center"
          animate={{ opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="w-16 h-16 rounded-full border-2 border-[#C4963B] flex items-center justify-center bg-black/30 backdrop-blur-sm"
            animate={{ scale: hovered ? 1 : 0.8, opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.25 }}
          >
            <Play className="w-6 h-6 text-[#C4963B] ml-0.5" fill="#C4963B" />
          </motion.div>
        </motion.div>
        {/* Tag */}
        <div className="absolute top-3 left-3">
          <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 ${tagColors[video.tag] || "bg-white/10 text-white/70"}`}>
            {video.tag}
          </span>
        </div>
        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/60 to-transparent" />
      </div>
      <h3 className="text-sm sm:text-base font-medium text-[#F9F6F0] mb-1.5 group-hover:text-[#C4963B] transition-colors duration-300 leading-snug line-clamp-2">
        {video.title}
      </h3>
      <p className="text-xs text-[#E8E6E1]/30 leading-snug">{video.venue}</p>
    </motion.div>
  );
}

export default function KeynoteSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [activeVideo, setActiveVideo] = useState(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [prevEnabled, setPrevEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(true);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setPrevEnabled(emblaApi.canScrollPrev());
      setNextEnabled(emblaApi.canScrollNext());
    };
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => { emblaApi.off("select", onSelect); emblaApi.off("reInit", onSelect); };
  }, [emblaApi]);

  return (
    <section data-testid="keynote-section" ref={ref} id="speaking" className="py-20 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <motion.div
          className="flex items-end justify-between mb-10 sm:mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div>
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-3 sm:mb-4">
              On Stage & On Screen
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl leading-tight">
              Watch Joe in action.
            </h2>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="https://www.youtube.com/@PwC"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 text-xs text-[#C4963B]/60 hover:text-[#C4963B] transition-colors tracking-wide mr-4"
            >
              All videos <ExternalLink className="w-3 h-3" />
            </a>
            <motion.button
              onClick={scrollPrev}
              disabled={!prevEnabled}
              className={`w-10 h-10 sm:w-11 sm:h-11 border flex items-center justify-center transition-all duration-300 ${prevEnabled ? "border-white/15 text-[#E8E6E1]/50 hover:border-[#C4963B] hover:text-[#C4963B]" : "border-white/[0.05] text-white/15 cursor-not-allowed"}`}
              whileTap={prevEnabled ? { scale: 0.92 } : {}}
            >
              <ChevronLeft className="w-4 h-4" />
            </motion.button>
            <motion.button
              onClick={scrollNext}
              disabled={!nextEnabled}
              className={`w-10 h-10 sm:w-11 sm:h-11 border flex items-center justify-center transition-all duration-300 ${nextEnabled ? "border-white/15 text-[#E8E6E1]/50 hover:border-[#C4963B] hover:text-[#C4963B]" : "border-white/[0.05] text-white/15 cursor-not-allowed"}`}
              whileTap={nextEnabled ? { scale: 0.92 } : {}}
            >
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Embla Carousel */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.15 }}
      >
        <div className="embla px-5 sm:px-8 md:px-12" ref={emblaRef}>
          <div className="embla__container">
            {videos.map((video, i) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.06 * i }}
              >
                <VideoCard
                  video={video}
                  onPlay={() => setActiveVideo(video)}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Scroll dots / progress */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <motion.div
          className="flex items-center gap-2 mt-8"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {scrollSnaps.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi?.scrollTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === selectedIndex
                  ? "w-8 bg-[#C4963B]"
                  : "w-2 bg-white/15 hover:bg-white/30"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
          <span className="ml-auto text-[10px] text-[#E8E6E1]/20 tabular-nums">
            {String(selectedIndex + 1).padStart(2, "0")} / {String(videos.length).padStart(2, "0")}
          </span>
        </motion.div>
      </div>

      <AnimatePresence>
        {activeVideo && (
          <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}
